import { Injectable, Injector } from '@angular/core'
import { PlatformService } from '../platforms/platform.service'
import { LogService } from 'htv-lib'
import { StorageService } from './storage.service'
import {
  GoogleCastConfig,
  GoogleCastIntegratedMode,
  GoogleCastProxyPage,
  GoogleCastProxyServerType,
  GoogleCastType,
} from '../interfaces/htv-config'
import { BehaviorSubject, Subscription } from 'rxjs'
import { RouterService, RouterPage } from './router.service'
import { GoogleCastQRCodeMode, GoogleCastProxyServerState, GoogleCastState } from '../interfaces/local-googlecast'
import { TranslateService } from '@ngx-translate/core'
import { ExternalInputService } from './external-input.service'
import { CastServer } from './cast-server'
import { CastIQCast } from './cast-iqcast'
import { CastVDCast } from './cast-vdcast'

@Injectable({
  providedIn: 'root',
})
export class CastService {
  readonly gcInitializedKey = 'googlecastInitialized'
  readonly gcToSAcceptedKey = 'googlecastToSAccepted'
  readonly gcUsageReportSetKey = 'googlecastUsageReportSet'

  // Config properties
  enabled = false
  type!: GoogleCastType
  hdmiPort?: number
  proxyServerType?: GoogleCastProxyServerType
  integratedMode?: GoogleCastIntegratedMode
  proxyStatusEndpoint?: string
  proxyPage?: GoogleCastProxyPage
  proxyRemoteEndpoint?: string

  // State properties
  isLoading = false
  gcInitialized = false // token registered & app installed
  gcToSAccepted = false
  gcUsageReportSet = false
  errorTitle?: string
  errorKey?: string

  gcInitializedSubject = new BehaviorSubject<boolean | undefined>(undefined)
  gcInitializedSubscription?: Subscription

  proxyServer!: CastServer
  isProxyReady = false

  roomNumber!: string
  tvName!: string // TODO: name of TV that will be shown in GoogleCast list on SmartPhone
  qrcodeMode: GoogleCastQRCodeMode = GoogleCastQRCodeMode.CODE

  pairingStatus = false
  pairingStatus$!: BehaviorSubject<boolean>

  code?: string
  qrcodeUrl?: string
  registerUrl?: string
  wifiSSID?: string
  wifiPassword?: string

  gcState?: GoogleCastState
  gcState$!: BehaviorSubject<GoogleCastState | undefined>

  pollProxyStatusInterval: any
  readonly pollProxyStatusIntervalDelayInMs = 60000
  pollProxyStatusUntilPairedTimer: any

  constructor(
    private externalInputService: ExternalInputService,
    private injector: Injector,
    private logService: LogService,
    private platformService: PlatformService,
    private routerService: RouterService,
    private storageService: StorageService,
    private translateService: TranslateService
  ) {
    // do nothing
  }

  async init(deviceName: string, config?: GoogleCastConfig): Promise<void> {
    this.logService.debug('[CastService] Initializing')

    // init saved session values
    this.gcInitialized = this.storageService.getBoolean(this.gcInitializedKey) ?? false
    this.gcToSAccepted = this.storageService.getBoolean(this.gcToSAcceptedKey) ?? false
    this.gcUsageReportSet = this.storageService.getBoolean(this.gcUsageReportSetKey) ?? false

    this.logService.debug(`[CastService] gcInitialized = ${this.gcInitialized}`)
    this.logService.debug(`[CastService] gcToSAccepted = ${this.gcToSAccepted}`)
    this.logService.debug(`[CastService] gcUsageReportSet = ${this.gcUsageReportSet}`)

    // Initialize configuration values
    this.enabled = config?.enabled ?? false
    this.type = config?.type ?? GoogleCastType.DONGLE
    this.hdmiPort = config?.hdmiPort ?? undefined
    this.integratedMode = config?.integratedMode ?? GoogleCastIntegratedMode.PROXY
    this.proxyServerType = config?.proxyServerType ?? GoogleCastProxyServerType.VDCAST
    this.proxyStatusEndpoint = config?.proxyStatusEndpoint ?? undefined
    this.proxyPage =
      this.proxyServerType == GoogleCastProxyServerType.IQCAST
        ? GoogleCastProxyPage.LOCAL
        : (config?.proxyPage ?? GoogleCastProxyPage.LOCAL)
    this.proxyRemoteEndpoint = config?.proxyRemoteEndpoint ?? undefined

    this.logService.debug(`[CastService] enabled = ${this.enabled}`)
    this.logService.debug(`[CastService] type = ${this.type}`)
    this.logService.debug(`[CastService] hdmiPort = ${this.hdmiPort}`)
    this.logService.debug(`[CastService] integratedMode = ${this.integratedMode}`)
    this.logService.debug(`[CastService] proxyServerType = ${this.proxyServerType}`)
    this.logService.debug(`[CastService] proxyStatusEndpoint = ${this.proxyStatusEndpoint}`)
    this.logService.debug(`[CastService] proxyPage = ${this.proxyPage}`)
    this.logService.debug(`[CastService] proxyRemoteEndpoint = ${this.proxyRemoteEndpoint}`)

    this.roomNumber = deviceName
    this.tvName = `[TV] ${this.roomNumber}`
    this.qrcodeMode = GoogleCastQRCodeMode.CODE // TODO: Should be configured in Humecloud ?

    this.loadProxyServer(this.proxyServerType, config)

    this.pairingStatus$ = new BehaviorSubject<boolean>(this.pairingStatus)
    this.updateGCState()

    if (!this.enabled) {
      this.logService.info('[CastService] Cast is disabled in configuration')
      return
    }

    switch (this.type) {
      case GoogleCastType.DONGLE:
        this.registerGoogleCastInitialized(true)
        this.logService.debug('[CastService] Nothing to initialize for Dongle type')
        break
      case GoogleCastType.INTEGRATED:
        if (this.platformService.hasIntegratedGoogleCast()) {
          try {
            this.initGoogleCastApp()
          } catch (err: any) {
            this.registerGoogleCastInitialized(false)
            this.logService.error(`[CastService] Could not initialize GoogleCast app, error = ${err?.message}`)
          }
        } else {
          this.registerGoogleCastInitialized(false)
          this.logService.error('[CastService] Platform does not support integrated Cast')
        }
        break
      default:
        this.registerGoogleCastInitialized(false)
        this.logService.error(`[CastService] Unknown type: ${this.type}`)
        break
    }
  }

  loadProxyServer(serverType: GoogleCastProxyServerType, config?: GoogleCastConfig): void {
    switch (serverType) {
      case GoogleCastProxyServerType.IQCAST:
        this.proxyServer = this.injector.get(CastIQCast)
        break
      case GoogleCastProxyServerType.VDCAST:
        this.proxyServer = this.injector.get(CastVDCast)
        break
      default:
        this.proxyServer = this.injector.get(CastVDCast)
        break
    }
    this.proxyServer.init(config)
  }

  clearGuestData(): void {
    try {
      this.platformService.clearGoogleCastData()
    } catch (err: any) {
      this.logService.error(`[CastService] Could not reset Cast data, error = ${err?.message}`)
    }
    this.sendCheckoutToProxy()
    this.registerGoogleCastToSAccepted(false)
    this.registerGoogleCastUsageReportSet(false)
    if (this.pollProxyStatusInterval) {
      clearInterval(this.pollProxyStatusInterval)
    }
  }

  isCastAvailable(): boolean {
    return this.enabled && this.gcInitialized
  }

  registerGoogleCastInitialized(initialized: boolean): void {
    this.gcInitialized = initialized
    this.storageService.saveBoolean(this.gcInitializedKey, this.gcInitialized)
  }

  registerGoogleCastToSAccepted(accepted: boolean): void {
    this.gcToSAccepted = accepted
    this.storageService.saveBoolean(this.gcToSAcceptedKey, this.gcToSAccepted)
  }

  registerGoogleCastUsageReportSet(set: boolean): void {
    this.gcUsageReportSet = set
    this.storageService.saveBoolean(this.gcUsageReportSetKey, this.gcUsageReportSet)
  }

  initGoogleCastApp(): void {
    this.logService.debug('[CastService] Initializing integrated GoogleCast...')
    if (this.gcInitializedSubscription) {
      this.gcInitializedSubscription?.unsubscribe()
    }
    this.gcInitializedSubscription = this.gcInitializedSubject.subscribe(async (initialized?: boolean) => {
      if (initialized != undefined) {
        this.logService.debug(`[CastService] Google Cast initialization status changed to: ${initialized}`)
        this.registerGoogleCastInitialized(initialized)
        if (this.gcInitialized) {
          switch (this.integratedMode) {
            case GoogleCastIntegratedMode.PROXY:
              await this.platformService.initGoogleCastProxy()
              this.registerProxyStatusInterval()
              break
            case GoogleCastIntegratedMode.SOFT_AP:
              await this.platformService.initGoogleCastSoftAP()
              this.qrcodeMode = GoogleCastQRCodeMode.WIFI
              break
            default:
              // do nothing
              break
          }
        }
      }
    })
    this.platformService.registerGoogleCastInitializedListener(this.gcInitializedSubject)
    this.platformService.initGoogleCastApp()
  }

  async initGoogleCastSession(): Promise<void> {
    this.isLoading = true
    this.updateGCState()

    if (!this.gcToSAccepted) {
      try {
        await this.platformService.acceptGoogleCastTermsOfService()
        this.registerGoogleCastToSAccepted(true)
        this.logService.info(`[CastService] Successfully accepted Google Cast TOS`)
      } catch (err: any) {
        this.registerGoogleCastToSAccepted(false)
        this.isLoading = false
        this.updateGCState()
        this.logService.error(`[CastService] An error occurred during Google Cast TOS initialization`)
        return
      }
    }

    if (!this.gcUsageReportSet) {
      try {
        await this.platformService.enableGoogleCastUsageReporting()
        this.registerGoogleCastUsageReportSet(true)
        this.logService.info(`[CastService] Successfully enabled Google Cast usage report`)
      } catch (err: any) {
        this.registerGoogleCastUsageReportSet(false)
        this.isLoading = false
        this.updateGCState()
        this.logService.error(`[CastService] An error occurred during Google Cast usage report initialization`)
        return
      }
    }

    this.isLoading = false
    this.updateGCState()
  }

  async openCastPage(): Promise<void> {
    if (!this.isCastAvailable()) {
      this.logService.error('[CastService] Cast is not available')
      return
    }

    switch (this.type) {
      case GoogleCastType.DONGLE:
        this.startDongleGoogleCast()
        break
      case GoogleCastType.INTEGRATED:
        this.startIntegratedGoogleCast()
        break
      default:
        this.logService.error(`[CastService] Unknown type: ${this.type}`)
        break
    }
  }

  startDongleGoogleCast(): void {
    if (this.hdmiPort) {
      try {
        this.routerService.goToPage(RouterPage.CAST_DONGLE)
        this.externalInputService.showHDMI(this.hdmiPort)
      } catch (err: any) {
        this.logService.error(`Could not start dongle cast, error = ${err?.message}`)
      }
    } else {
      this.logService.info('[CastService] No HDMI port has been configured for Dongle Cast')
    }
  }

  async startIntegratedGoogleCast(): Promise<void> {
    if (!this.gcInitialized) {
      this.errorKey = 'GC_NOT_INITIALIZED'
      this.updateGCState()
      return
    }

    this.initGoogleCastSession()

    switch (this.integratedMode) {
      case GoogleCastIntegratedMode.PROXY: {
        this.logService.info(`[CastService] Starting Google Cast Proxy page`)
        this.pollProxyStatusUntilPaired()
        switch (this.proxyPage) {
          case GoogleCastProxyPage.LOCAL: {
            const supportMessage = this.translateService.instant('CAST.SUPPORT_MESSAGE')
            this.platformService.openGoogleCastLocalPage(this.roomNumber, supportMessage, this.gcState$)
            break
          }
          case GoogleCastProxyPage.REMOTE: {
            this.routerService.goToPage(RouterPage.EXTERNAL_LINK, {
              queryParams: { actionNavigationUrl: this.proxyRemoteEndpoint },
            })
            break
          }
          default:
            this.logService.error(`[CastService] Unknown GoogleCastProxyPage: ${this.proxyPage}`)
            break
        }
        break
      }
      case GoogleCastIntegratedMode.SOFT_AP: {
        this.logService.info(`[CastService] Starting Google Cast SoftAP page`)
        const pairingStatus = false
        await this.pollSoftAP()
        const supportMessage = this.translateService.instant('CAST.SUPPORT_MESSAGE')
        this.platformService.openGoogleCastSoftAPPage(this.roomNumber, pairingStatus, supportMessage)
        break
      }
      default:
        this.logService.error(`[CastService] Unknown ingrated mode: ${this.integratedMode}`)
        break
    }
  }

  stopCast(): void {
    switch (this.type) {
      case GoogleCastType.DONGLE:
        this.platformService.hideHDMI()
        break
      case GoogleCastType.INTEGRATED:
        break
      default:
        this.logService.error(`[CastService] Unknown type: ${this.type}`)
        break
    }
  }

  launchGoogleCastSettings(): void {
    this.platformService.launchGoogleCastSettings()
  }

  pollProxyStatusUntilPaired(count = 0): void {
    if (this.pollProxyStatusUntilPairedTimer) {
      clearTimeout(this.pollProxyStatusUntilPairedTimer)
    }

    const registerTimer = (delay: number) => {
      this.pollProxyStatusUntilPairedTimer = setTimeout(() => {
        this.pollProxyStatusUntilPaired(++count)
      }, delay)
    }

    if (count < 60) {
      this.queryProxyStatus()
      registerTimer(1000)
    }
  }

  registerProxyStatusInterval(): void {
    if (this.pollProxyStatusInterval) {
      clearInterval(this.pollProxyStatusInterval)
    }
    this.queryProxyStatus()
    this.pollProxyStatusInterval = setInterval(() => {
      this.queryProxyStatus()
    }, this.pollProxyStatusIntervalDelayInMs)
  }

  queryProxyStatus(): void {
    this.proxyServer.queryProxyStatus().subscribe((res: GoogleCastProxyServerState | undefined) => {
      this.clearStateValues()
      if (res === undefined) {
        this.updateGCState()
      } else {
        this.isProxyReady = !res.hasError
        this.pairingStatus = res.pairingStatus
        this.code = res.code
        this.qrcodeUrl = res.qrcodeUrl
        this.registerUrl = res.registerUrl
        this.wifiSSID = res.wifiSSID
        this.errorKey = res.errorKey
        this.updateGCState()

        if (res.pairingStatus && res.pairingStatus !== this.pairingStatus) {
          this.pairingStatus = res.pairingStatus
          this.pairingStatus$.next(this.pairingStatus)
        }
      }
    })
  }

  sendCheckoutToProxy(): void {
    this.logService.info(`[CastService] Sending checkout to Cast proxy`)
    this.proxyServer.sendCheckoutToProxy().subscribe(() => {
      this.queryProxyStatus()
    })
  }

  async pollSoftAP(): Promise<void> {
    const result = await this.platformService.pollGoogleCastSoftAP()
    this.clearStateValues()
    if (result) {
      this.pairingStatus = false
      this.wifiSSID = result?.ssid
      this.wifiPassword = result?.password
    }
    this.updateGCState()
  }

  clearStateValues(): void {
    this.errorTitle = undefined
    this.errorKey = undefined
    this.pairingStatus = false
    this.code = undefined
    this.qrcodeUrl = undefined
    this.registerUrl = undefined
    this.wifiSSID = undefined
    this.wifiPassword = undefined
  }

  updateGCState(): void {
    const gcState: GoogleCastState = {
      isLoading: this.isLoading,
      isReady: this.gcInitialized && this.gcToSAccepted && this.gcUsageReportSet,
      isProxyReady: this.isProxyReady,
      gcInitialized: this.gcInitialized,
      gcToSAccepted: this.gcToSAccepted,
      gcUsageReportSet: this.gcUsageReportSet,
      errorTitle: this.errorTitle,
      errorKey: this.errorKey,
      pairingStatus: this.pairingStatus,
      code: this.code?.replace(/.{3}$/, ' $&'),
      qrcodeUrl: this.qrcodeUrl,
      registerUrl: this.registerUrl,
      wifiSSID: this.wifiSSID,
      wifiPassword: this.wifiPassword,
    }
    if (JSON.stringify(this.gcState) != JSON.stringify(gcState)) {
      this.gcState = gcState
      if (this.gcState$) {
        this.gcState$.next(this.gcState)
      } else {
        this.gcState$ = new BehaviorSubject<GoogleCastState | undefined>(this.gcState)
      }
    }
  }
}
